import { Alert, Box, Button, Grid, Typography } from '@mui/material'
import { IconPlusCircle } from 'assets/images'
import { t } from 'i18n'
import React, { useState } from 'react'
import { toast } from 'react-toastify'
import { uploadHeatmap } from 'services'
import { HeatmapDefinition } from 'store/ducks/report/types'
import HeatmapCard from './components/HeatmapCard'
import CreateHeatmapDialog from './components/dialogs/CreateHeatmapDialog'
import FullSizePreviewDialog from './components/dialogs/FullSizePreviewDialog'
import RenameHeatmapDialog from './components/dialogs/RenameHeatmapDialog'
import ReplaceConfirmationDialog from './components/dialogs/ReplaceConfirmationDialog'
import { useHeatmaps } from './hooks/useHeatmaps'

const HeatmapManager: React.FC = () => {
  // Use custom hooks for state management
  const {
    heatmaps,
    loading,
    selectedHeatmapIds,
    heatmapsBySeason,
    handleSetDefault,
    handleDelete,
    handleRename,
    handleCreate,
    handleHeatmapSelection,
    refreshHeatmaps
  } = useHeatmaps()

  // Dialog states
  const [renameDialog, setRenameDialog] = useState<{
    open: boolean
    heatmap?: HeatmapDefinition
  }>({ open: false })

  const [createDialog, setCreateDialog] = useState({ open: false })

  const [fullSizeDialog, setFullSizeDialog] = useState<{
    open: boolean
    heatmap?: HeatmapDefinition
  }>({ open: false })

  const [replaceDialog, setReplaceDialog] = useState<{
    open: boolean
    heatmap?: HeatmapDefinition
    loading?: boolean
  }>({ open: false })

  // Event handlers
  const handleRenameOpen = (heatmap: HeatmapDefinition) => {
    setRenameDialog({ open: true, heatmap })
  }

  const handleRenameClose = () => {
    setRenameDialog({ open: false })
  }

  const handleRenameConfirm = async (heatmapId: string, newName: string) => {
    await handleRename(heatmapId, newName)
  }

  const handleCreateOpen = () => {
    setCreateDialog({ open: true })
  }

  const handleCreateClose = () => {
    setCreateDialog({ open: false })
  }

  const handleCreateSuccess = async (params: {
    name: string
    season: string
    file: File
  }) => {
    // 1) Create heatmap and get PUT URL
    const { putUrl, id } = await handleCreate({
      name: params.name,
      season: params.season,
      fileName: params.file.name
    })

    // 2) Upload file to S3
    const res = await fetch(putUrl, {
      method: 'PUT',
      body: params.file
    })
    if (!res.ok) throw new Error('Upload failed')

    // 3) Refresh list and auto-select
    await refreshHeatmaps()
    handleHeatmapSelection(params.season, id)

    toast.success(t('heatmaps.saved') || t('save') || 'Saved')
  }

  const handlePreviewClick = (heatmap: HeatmapDefinition) => {
    setFullSizeDialog({ open: true, heatmap })
  }

  const handleFullSizeClose = () => {
    setFullSizeDialog({ open: false })
  }

  const handleReplace = async (heatmap: HeatmapDefinition) => {
    setReplaceDialog({ open: true, heatmap })
  }

  const handleReplaceConfirm = async (file: File) => {
    const { heatmap } = replaceDialog
    if (!heatmap || !file) return

    // Set loading state
    setReplaceDialog(prev => ({ ...prev, loading: true }))

    try {
      // 1) Get PUT presigned URL for existing key
      const put = await uploadHeatmap({
        fileName: heatmap.fileName,
        type: 'PUT_OBJECT',
        relatedRecordType: 'RESORT',
        season: heatmap.season.toLowerCase()
      })
      if (!put?.url) throw new Error('No PUT URL')

      // 2) Upload file
      const res = await fetch(put.url, {
        method: 'PUT',
        body: file
      })
      if (!res.ok) throw new Error('Upload failed')

      // 3) Refresh heatmaps list and reload page to clear cache
      await refreshHeatmaps()
      toast.success(t('heatmaps.saved') || t('save') || 'Saved')

      // Close dialog before reloading
      setReplaceDialog({ open: false })
      window.location.reload()
    } catch (err: any) {
      // Reset loading state on error
      setReplaceDialog(prev => ({ ...prev, loading: false }))
      toast.error(
        err?.response?.data?.message || err?.message || 'Upload failed'
      )
    }
  }

  const handleReplaceClose = () => {
    setReplaceDialog({ open: false })
  }

  if (loading && heatmaps.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <Typography>Loading heatmaps...</Typography>
      </Box>
    )
  }

  return (
    <Box style={{ width: '100%' }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5" component="h2">
          {t('heatmaps.manager') || 'Heatmap Manager'}
        </Typography>
        <Button
          variant="outlined"
          onClick={handleCreateOpen}
          size="small"
          startIcon={<IconPlusCircle width="1em" fill="currentColor" />}
        >
          {t('heatmaps.upload') || 'Upload Heatmap'}
        </Button>
      </Box>

      {Object.keys(heatmapsBySeason).length === 0 ? (
        <Alert severity="info">
          {t('heatmaps.noFound') ||
            'No heatmaps found. Upload your first heatmap to get started.'}
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {Object.entries(heatmapsBySeason).map(([season, seasonHeatmaps]) => (
            <Grid item xs={12} md={6} key={season}>
              <HeatmapCard
                season={season}
                heatmaps={seasonHeatmaps}
                selectedHeatmapId={selectedHeatmapIds[season]}
                onHeatmapSelection={handleHeatmapSelection}
                onSetDefault={handleSetDefault}
                onReplace={handleReplace}
                onRename={handleRenameOpen}
                onDelete={handleDelete}
                onPreviewClick={handlePreviewClick}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialogs */}
      <RenameHeatmapDialog
        open={renameDialog.open}
        heatmap={renameDialog.heatmap}
        onClose={handleRenameClose}
        onConfirm={handleRenameConfirm}
      />

      <CreateHeatmapDialog
        open={createDialog.open}
        onClose={handleCreateClose}
        onSuccess={handleCreateSuccess}
      />

      <FullSizePreviewDialog
        open={fullSizeDialog.open}
        heatmap={fullSizeDialog.heatmap}
        onClose={handleFullSizeClose}
      />

      <ReplaceConfirmationDialog
        open={replaceDialog.open}
        loading={replaceDialog.loading}
        onClose={handleReplaceClose}
        onConfirm={handleReplaceConfirm}
      />
    </Box>
  )
}

export default HeatmapManager
